import { useCallback, useEffect, useRef, useState } from 'react';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  QueryDocumentSnapshot,
  startAfter
} from 'firebase/firestore';
import { AppUser } from '../types';
import {
  addMediaComment,
  deleteMediaComment,
  hasMediaCommentLike,
  MediaComment,
  toggleMediaCommentLike
} from '../backend/mediaService';
import { db } from '../backend/firebase';
import { withReadCounter } from '../utils/withReadCounter';

export function usePostComments(
  postId: string,
  currentUser: AppUser | null,
  pageSize = 5
) {
  const [comments, setComments] = useState<MediaComment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const cursorRef = useRef<QueryDocumentSnapshot | null>(null);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const commentsRef = useRef<MediaComment[]>([]);
  const likedInFlightRef = useRef(new Set<string>());
  const generationRef = useRef(0);
  const startedKeyRef = useRef<string | null>(null);
  const currentUserId = currentUser?.uid;
  const requestKey = `${postId}:${currentUserId || 'anonymous'}`;

  const loadPage = useCallback(async (key: string, generation: number) => {
    if (loadingRef.current || !hasMoreRef.current) return;
    loadingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const constraints = [
        orderBy('createdAt', 'desc'),
        ...(cursorRef.current ? [startAfter(cursorRef.current)] : []),
        limit(pageSize)
      ];
      const snapshot = await withReadCounter(
        'post-comments',
        () => getDocs(query(collection(db, 'media', postId, 'comments'), ...constraints)),
        result => ({ reads: result.metadata.fromCache ? 0 : result.size })
      );
      const page = await Promise.all(snapshot.docs.map(async document => {
        const data = document.data();
        const comment: MediaComment = {
          id: document.id,
          uid: data.uid || data.userId || '',
          userId: data.userId || data.uid || '',
          userName: data.userName || 'Clue200 Member',
          userAvatar: data.userAvatar || '',
          text: data.text || '',
          createdAt: data.createdAt,
          likesCount: Number(data.likesCount ?? data.likes ?? 0),
          isLiked: false
        };
        if (currentUserId) {
          comment.isLiked = await hasMediaCommentLike(postId, document.id, currentUserId, 'post-comments');
        }
        return comment;
      }));

      if (generationRef.current !== generation || requestKey !== key) return;
      const seen = new Set(commentsRef.current.map(comment => comment.id));
      const uniquePage = page.filter(comment => !seen.has(comment.id));
      commentsRef.current = [...commentsRef.current, ...uniquePage];
      setComments(commentsRef.current);
      cursorRef.current = snapshot.docs.at(-1) ?? cursorRef.current;
      const canContinue = snapshot.docs.length === pageSize;
      hasMoreRef.current = canContinue;
      setHasMore(canContinue);
    } catch (loadError) {
      if (generationRef.current === generation && requestKey === key) {
        setError(loadError instanceof Error ? loadError : new Error('Could not load comments.'));
      }
    } finally {
      if (generationRef.current === generation && requestKey === key) {
        loadingRef.current = false;
        setIsLoading(false);
      }
    }
  }, [currentUserId, pageSize, postId, requestKey]);

  useEffect(() => {
    if (startedKeyRef.current === requestKey) return;
    startedKeyRef.current = requestKey;
    const generation = ++generationRef.current;
    cursorRef.current = null;
    commentsRef.current = [];
    loadingRef.current = false;
    hasMoreRef.current = true;
    likedInFlightRef.current.clear();
    setComments([]);
    setIsLoading(true);
    setHasMore(true);
    setError(null);
    void loadPage(requestKey, generation);
  }, [loadPage, requestKey]);

  const loadMore = useCallback(() => {
    if (!hasMoreRef.current || loadingRef.current) return;
    void loadPage(requestKey, generationRef.current);
  }, [loadPage, requestKey]);

  const addComment = useCallback(async (text: string) => {
    if (!currentUser) throw new Error('Sign in to comment.');
    const cleanText = text.trim();
    if (!cleanText) return;
    const temporaryId = `pending-${Date.now()}-${Math.random()}`;
    const optimisticComment: MediaComment = {
      id: temporaryId,
      uid: currentUser.uid,
      userId: currentUser.uid,
      userName: currentUser.displayName || 'You',
      userAvatar: currentUser.photoURL || '',
      text: cleanText,
      createdAt: new Date(),
      likesCount: 0,
      isLiked: false
    };
    commentsRef.current = [optimisticComment, ...commentsRef.current];
    setComments(commentsRef.current);

    try {
      const commentId = await addMediaComment(postId, currentUser, cleanText);
      commentsRef.current = commentsRef.current.map(comment =>
        comment.id === temporaryId ? { ...comment, id: commentId } : comment
      );
      setComments(commentsRef.current);
    } catch (addError) {
      commentsRef.current = commentsRef.current.filter(comment => comment.id !== temporaryId);
      setComments(commentsRef.current);
      throw addError;
    }
  }, [currentUser, postId]);

  const deleteComment = useCallback(async (commentId: string) => {
    if (!currentUser) throw new Error('Sign in to delete your comment.');
    const previousComment = commentsRef.current.find(comment => comment.id === commentId);
    if (!previousComment) return;
    commentsRef.current = commentsRef.current.filter(comment => comment.id !== commentId);
    setComments(commentsRef.current);
    try {
      await deleteMediaComment(postId, commentId, currentUser.uid);
    } catch (deleteError) {
      commentsRef.current = [previousComment, ...commentsRef.current];
      setComments(commentsRef.current);
      throw deleteError;
    }
  }, [currentUser, postId]);

  const toggleCommentLike = useCallback(async (commentId: string) => {
    if (!currentUser) throw new Error('Sign in to like comments.');
    if (likedInFlightRef.current.has(commentId)) return;
    likedInFlightRef.current.add(commentId);
    const previousComment = commentsRef.current.find(comment => comment.id === commentId);
    if (!previousComment) {
      likedInFlightRef.current.delete(commentId);
      return;
    }
    const wasLiked = previousComment.isLiked;
    const optimisticLiked = !wasLiked;
    commentsRef.current = commentsRef.current.map(comment =>
      comment.id === commentId
        ? {
            ...comment,
            isLiked: optimisticLiked,
            likesCount: Math.max(0, comment.likesCount + (wasLiked ? -1 : 1))
          }
        : comment
    );
    setComments(commentsRef.current);

    try {
      const isLiked = await toggleMediaCommentLike(postId, commentId, currentUser.uid);
      if (isLiked !== optimisticLiked) {
        const correction = (isLiked ? 1 : 0) - (optimisticLiked ? 1 : 0);
        commentsRef.current = commentsRef.current.map(comment =>
          comment.id === commentId
            ? {
                ...comment,
                isLiked,
                likesCount: Math.max(0, comment.likesCount + correction)
              }
            : comment
        );
        setComments(commentsRef.current);
      }
    } catch (likeError) {
      commentsRef.current = commentsRef.current.map(comment =>
        comment.id === commentId
          ? { ...comment, isLiked: wasLiked, likesCount: previousComment.likesCount }
          : comment
      );
      setComments(commentsRef.current);
      throw likeError;
    } finally {
      likedInFlightRef.current.delete(commentId);
    }
  }, [currentUser, postId]);

  return { comments, addComment, deleteComment, loadMore, toggleCommentLike, isLoading, hasMore, error };
}
